// OverlayRowsView — reine View: baut die Admin-Overlay-Listenzeilen aus reinen Daten.
// Kein Event-Wiring, kein AJAX, keine Karte, kein State. Einzige Quelle des Zeilen-Markups.
//
// Erster Bewohner der view/-Schicht: alles Grafische soll perspektivisch hierher (weg aus dem
// Map-Core), später in den Core injiziert. Siehe Plan topomapcore_view-extraction.md.
//
// i18n: user-sichtbare Labels/Titel laufen über str('OVERLAY_*') (Interim-Helper wie in den anderen
// Modulen; fehlt der Key in der DB, bleibt der Key sichtbar — DB-Strings folgen). Symbole (°, ⠿, ?) bleiben Literale.
import { str } from "../../i18n/str.js";

/**
 * @typedef {Object} OverlayRow
 * @property {string}        ref       präfixierte Zeilen-Ref ('m<id>'|'o<id>') = DOM-id-Suffix
 * @property {string}        name
 * @property {string}        year
 * @property {string}        uploader
 * @property {string}        image     Bild-URL
 * @property {boolean}       placed    auf der Karte platziert
 * @property {boolean}       visible   im Frontend sichtbar
 * @property {string}        zoom      "min-max"
 * @property {number|string} rotation  Grad
 * @property {string}        sw        "lat,lng" oder '' (Hidden-Input, Save-Seam)
 * @property {string}        ne        "lat,lng" oder ''
 * @property {string|number} latSW
 * @property {string|number} lngSW
 * @property {string|number} latNE
 * @property {string|number} lngNE
 * @property {string}        distort   JSON der Verzerr-Ecken oder ''
 */

const CHEVRON_ICON ='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const TRASH_ICON = '<i class="topo2-garbage"></i>';
const GLOBE_ICON = '<span class="topo2-ic topo2-ic--globe"></span>';
const EXIT_ICON = '<span class="topo2-ic topo2-ic--exit"></span>';

// Minimaler Element-Builder. Dynamische Werte gehen als Property/textContent rein → der Browser
// escaped automatisch (kein Handarbeit-Escaping, keine Injection-Lücke). null/false = Attribut weglassen.
function h(tag, attrs, ...kids) {
    const el = document.createElement(tag);
    for (const k in attrs) {
        const v = attrs[k];
        if (v == null || v === false) { continue; }
        if (k === 'class') { el.className = v; }
        else if (k in el) { el[k] = v; }            // value, checked, disabled, src, title, id, name …
        else { el.setAttribute(k, v); }             // data-id u.ä.
    }
    for (const kid of kids.flat()) {
        if (kid == null || kid === false) { continue; }
        el.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    }
    return el;
}

// Statisches SVG/Icon einmalig setzen (kein Datenwert → unkritisch).
function withIcon(el, svg) { el.innerHTML = svg; return el; }

export function createOverlayRowsView() {

    /** @param {OverlayRow} r @returns {HTMLElement} eine .overlayRow */
    function renderRow(r) {
        const id = r.ref;
        return h('div', { class: 'overlayRow ' + (r.placed ? 'placed' : 'notplaced'),
                          id: 'tr' + id, 'data-id': id, title: r.placed ? null : str('OVERLAY_NOT_ON_MAP') },

            // Spalte 1 — Auge (auf der Editor-Karte zeigen, nur Ansicht) + Sortier-Griff
            h('div', { class: 'handleCell' },
                h('label', { class: 'visibleEye', title: str('OVERLAY_SHOW_IN_EDITOR') },
                    h('input', { type: 'checkbox', class: 'editorVisibleToggle', id: 'editorVisible' + id, checked: true }),
                    h('i', { class: 'topo2-eye' })),
                h('div', { class: 'sortHandle topo2-handle', title: str('OVERLAY_SORT_HANDLE') },
                    h('span', { class: 'layerNo', title: str('OVERLAY_LAYER') }, '?'),
                    h('span', { class: 'sortGrip' }, '⠿'))),

            // Spalte 2 — Name / Meta / Bild + Hidden-Inputs (Save-Seam)
            h('div', { class: 'overlayDataCell' },
                h('div', { class: 'overlayTitleRow topo2-field-row' },
                    h('label', { class: 'metaLabel topo2-field-label', htmlFor: 'name' + id }, str('OVERLAY_FIELD_NAME') + ':'),
                    h('input', { id: 'name' + id, name: 'name' + id, type: 'text', value: r.name,
                                 class: 'overlayTitle marksDirty topo2-field-textinput' })),
                h('div', { class: 'overlayMeta' },
                    h('div', { class: 'topo2-field-row' },
                        h('label', { class: 'metaLabel topo2-field-label', htmlFor: 'year' + id }, str('OVERLAY_FIELD_YEAR') + ':'),
                        h('input', { id: 'year' + id, name: 'year' + id, type: 'text', value: r.year,
                                     class: 'marksDirty topo2-field-textinput' })),
                    h('div', { class: 'topo2-field-row' },
                        h('label', { class: 'metaLabel topo2-field-label', htmlFor: 'infoText' + id }, str('OVERLAY_FIELD_INFO') + ':'),
                        h('textarea', { id: 'infoText' + id, name: 'infoText' + id, rows: 3,
                                        class: 'marksDirty topo2-field-textarea' }, r.info_text || '')),
                    h('div', { class: 'topo2-field-row' },
                        h('label', { class: 'metaLabel topo2-field-label', htmlFor: 'uploader' + id }, str('OVERLAY_FIELD_UPLOADER') + ':'),
                        h('input', { id: 'uploader' + id, name: 'uploader' + id, type: 'text', value: r.uploader, disabled: true,
                                     class: 'overlayUploader topo2-field-textinput' })),
                    // „ID:" ist eine Caption — dahinter steht der ID-Wert als <span>, kein Input → bewusst KEIN <label for>.
                    h('div', { class: 'topo2-field-row' },
                        h('span', { class: 'metaLabel topo2-field-label' }, str('OVERLAY_FIELD_ID') + ':'),
                        h('span', { class: 'overlayId topo2-field-label', title: str('OVERLAY_ID_HINT') }, id),
                        h('span', { class: 'notOnMapInfo' },
                            h('span', { class: 'topo2-ic topo2-ic--warning', 'aria-hidden': 'true' }),
                            str('OVERLAY_NOT_ON_MAP_INFO')))),
                h('div', { class: 'overlayImageWrap' },
                    h('img', { id: 'image' + id, class: 'overlayImage', src: r.image })),
                // Save/Delete are no longer absolute corners inside the image — they live in their own
                // action row below the image wrap (class names dropped the "Corner" accordingly).
                h('div', { class: 'overlayRowActions' },
                    h('button', { type: 'button', class: 'save saveAction topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--solid topo2-btn--noborder', id: 'save' + id, title: str('OVERLAY_SAVE') }, 'OK'),   // "OK"-Text statt Disketten-Icon (wie der Edit-Toolbar-OK)
                    // Publish = shown in the frontend (column visible), a toggle (aria-pressed); the eye only shows/hides the
                    // overlay on the editor map (plan bugfix_2026-10-09_polishing-patch-2, phase overlay-auge).
                    withIcon(h('button', { type: 'button', class: 'publish publishAction topo2-btn topo2-btn--toggle topo2-btn--icon topo2-btn--small topo2-btn--outline topo2-btn--inset',
                                           id: 'publish' + id, title: str('OVERLAY_SHOW_IN_FRONTEND'), 'aria-pressed': r.visible ? 'true' : 'false' }), GLOBE_ICON),
                    // Remove from the map (like the exit button of the editor toolbar, but for this row) and delete stand
                    // together on the right; publish stays in the middle (User 2026-10-16).
                    h('span', { class: 'overlayRowActionsEnd' },
                        withIcon(h('button', { type: 'button', class: 'removeFromMapAction topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--solid topo2-btn--noborder', title: str('OVERLAY_REMOVE_FROM_MAP') }), EXIT_ICON),
                        withIcon(h('button', { type: 'button', class: 'delete deleteAction topo2-btn topo2-btn--icon topo2-btn--small topo2-btn--solid topo2-btn--noborder', id: 'delete' + id, title: str('OVERLAY_DELETE') }), TRASH_ICON))),
                h('input', { type: 'hidden', class: 'inpSW', name: 'SW' + id, id: 'inpSW' + id, value: r.sw }),
                h('input', { type: 'hidden', class: 'inpNE', name: 'NE' + id, id: 'inpNE' + id, value: r.ne }),
                h('input', { type: 'hidden', class: 'inpDistort', id: 'inpDistort' + id, value: r.distort }),
                h('input', { type: 'hidden', id: 'zoom' + id, value: r.zoom })),

            // Spalte 3 — klappbare Georeferenz / Rotation
            h('div', { class: 'mapDataCell' },
                withIcon(h('button', { type: 'button', class: 'mapDataToggle topo2-handle', 'aria-expanded': 'false', title: str('OVERLAY_GEOREF_TOGGLE') }), CHEVRON_ICON),
                h('table', { class: 'infoTable' },
                    h('tr', null,
                        h('td', null, str('OVERLAY_GEOREF') + ':'),
                        h('td', { class: 'georefCell' },
                            h('label', { class: 'georefCorner', htmlFor: 'geoLatSW' + id }, str('OVERLAY_CORNER_SW')),
                            h('input', { class: 'georefLat topo2-field-textinput', id: 'geoLatSW' + id, title: str('OVERLAY_LAT_SW'), value: r.latSW }),
                            h('input', { class: 'georefLng topo2-field-textinput', id: 'geoLngSW' + id, title: str('OVERLAY_LNG_SW'), value: r.lngSW }),
                            h('br'),
                            h('label', { class: 'georefCorner', htmlFor: 'geoLatNE' + id }, str('OVERLAY_CORNER_NE')),
                            h('input', { class: 'georefLat topo2-field-textinput', id: 'geoLatNE' + id, title: str('OVERLAY_LAT_NE'), value: r.latNE }),
                            h('input', { class: 'georefLng topo2-field-textinput', id: 'geoLngNE' + id, title: str('OVERLAY_LNG_NE'), value: r.lngNE }))),
                    h('tr', null,
                        h('td', null, h('label', { htmlFor: 'rotation' + id }, str('OVERLAY_ROTATION') + ':')),
                        h('td', null,
                            h('input', { id: 'rotation' + id, class: 'rotationInput topo2-field-textinput marksDirty', value: r.rotation, size: '6' }),
                            '°',
                            h('span', { class: 'hint' }, str('OVERLAY_ROTATION_HINT')))))));
    }

    /** Ersetzt die vorhandenen .overlayRow-Zeilen im host durch frische aus rows (Header bleibt). */
    function renderInto(host, rows) {
        host.querySelectorAll(':scope > .overlayRow').forEach((el) => el.remove());
        const frag = document.createDocumentFragment();
        (rows || []).forEach((r) => frag.append(renderRow(r)));
        host.append(frag);
    }

    return { renderInto, renderRow };
}
